import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { API_URL } from '../config';

const LidMappings = () => {
  const [unmappedLids, setUnmappedLids] = useState([]);
  const [allMappings, setAllMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLid, setEditingLid] = useState(null);
  const [formData, setFormData] = useState({ phoneNumber: '', name: '' });
  const [activeTab, setActiveTab] = useState('unmapped');

  const user = useSelector((state) => state.auth.user);
  const userId = user?.id || 1;

  const fetchLidMappings = useCallback(async () => {
    setLoading(true);
    try {
      const query = activeTab === 'unmapped'
        ? `query { unmappedLids(userId: "${userId}") { lid callCount messageCount lastSeen } }`
        : `query { allLidMappings(userId: "${userId}") { lid phoneNumber name isMapped callCount messageCount lastSeen } }`;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });

      const result = await response.json();

      if (activeTab === 'unmapped') {
        setUnmappedLids(result.data.unmappedLids || []);
      } else {
        setAllMappings(result.data.allLidMappings || []);
      }
    } catch (error) {
      console.error('Error fetching LID mappings:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, userId]);

  useEffect(() => {
    fetchLidMappings();
  }, [fetchLidMappings]);

  const handleEdit = (lid) => {
    setEditingLid(lid);
    const existing = allMappings.find(m => m.lid === lid);
    if (existing) {
      setFormData({ phoneNumber: existing.phoneNumber || '', name: existing.name || '' });
    } else {
      setFormData({ phoneNumber: '', name: '' });
    }
  };

  const handleSave = async (lid) => {
    try {
      const mutation = `
        mutation {
          updateLidMapping(input: {
            lid: "${lid}"
            phoneNumber: "${formData.phoneNumber}"
            name: "${formData.name}"
          }) {
            lid
            phoneNumber
            name
            isMapped
          }
        }
      `;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: mutation })
      });

      const result = await response.json();

      if (result.data.updateLidMapping) {
        setEditingLid(null);
        setFormData({ phoneNumber: '', name: '' });
        fetchLidMappings();
      }
    } catch (error) {
      console.error('Error updating LID mapping:', error);
    }
  };

  const handleDelete = async (lid) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;

    try {
      const mutation = `mutation { deleteLidMapping(lid: "${lid}") }`;

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: mutation })
      });

      const result = await response.json();

      if (result.data.deleteLidMapping) {
        fetchLidMappings();
      }
    } catch (error) {
      console.error('Error deleting mapping:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto animate-in fade-in duration-700">
      {/* Header Pipeline */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-bulky-blue-50/50 rounded-full -mr-32 -mt-32 blur-3xl opacity-50"></div>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 relative z-10">
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-1 h-5 bg-bulky-blue-500 rounded-full"></div>
              <h2 className="text-lg md:text-xl font-medium text-bulky-navy-800 tracking-tight uppercase leading-none">Terminal ID Resolver</h2>
            </div>
            <p className="text-bulky-navy-400 text-[8px] md:text-[9px] font-normal uppercase tracking-wider md:tracking-wider ml-4 flex items-center gap-2">
              <span className="material-icons-round text-sm text-bulky-blue-500">router</span>
              Map Linked Device IDs (LIDs) to Network Protocol Identifiers
            </p>
          </div>
        </div>
      </div>

      {/* Logic Context Box */}
      <div className="bg-bulky-navy-800 rounded-2xl p-6 mb-6 shadow-lg relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-bulky-blue-500/10 rounded-bl-full animate-pulse"></div>
        <div className="flex flex-col md:flex-row items-start gap-5 relative z-10">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-white/10 flex items-center justify-center text-bulky-blue-400 shrink-0 shadow-inner">
            <span className="material-icons-round text-2xl">info</span>
          </div>
          <div>
            <h2 className="font-medium">Protocol Brief: What is a LID?</h2>
            <p className="text-bulky-navy-200 text-[11px] font-normal leading-relaxed max-w-2xl opacity-80 uppercase tracking-tight">
              WhatsApp Web protocol masks incoming call identities. A <span className="text-bulky-blue-400 font-normal">Linked Device ID (LID)</span> is an encrypted placeholder. Mapping these to real phone numbers authorizes the system to execute automated response templates.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Matrix */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6 bg-gray-50/50 p-2 rounded-xl border border-gray-100 w-full sm:w-fit">
        {[
          { id: 'unmapped', label: 'Unresolved', icon: 'error_outline', count: unmappedLids.length, color: 'text-red-500' },
          { id: 'all', label: 'Full Resolution', icon: 'grid_view', count: allMappings.length, color: 'text-bulky-navy-600' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center justify-between sm:justify-start gap-4 px-5 py-2.5 rounded-lg font-normal text-[9px] uppercase tracking-wider transition-all duration-300 ${activeTab === tab.id
              ? 'bg-white shadow-sm text-bulky-blue-500'
              : 'text-gray-400 hover:text-bulky-navy-800'
              }`}
          >
            <div className="flex items-center gap-3">
              <span className={`material-icons-round text-base ${activeTab === tab.id ? 'text-bulky-blue-500' : 'text-gray-300'}`}>{tab.icon}</span>
              {tab.label}
            </div>
            {tab.count > 0 && (
              <span className={`ml-3 px-2 py-0.5 rounded text-[8px] font-normal ${activeTab === tab.id ? 'bg-bulky-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-24 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="w-12 h-12 border-4 border-bulky-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[10px] font-normal text-bulky-navy-300 uppercase tracking-wider">Decoding Identifiers...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-[9px] font-normal uppercase tracking-wider text-bulky-navy-300">Terminal LID</th>
                  {activeTab === 'all' && <th className="px-6 py-4 text-left text-[9px] font-normal uppercase tracking-wider text-bulky-navy-300">Identity</th>}
                  <th className="px-6 py-4 text-left text-[9px] font-normal uppercase tracking-wider text-bulky-navy-300">Metrics</th>
                  <th className="px-6 py-4 text-left text-[9px] font-normal uppercase tracking-wider text-bulky-navy-300 text-center">Status</th>
                  <th className="px-6 py-4 text-right text-[9px] font-normal uppercase tracking-wider text-bulky-navy-300 pr-8">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(activeTab === 'unmapped' ? unmappedLids : allMappings).map((lid) => (
                  <tr key={lid.lid} className="group hover:bg-bulky-blue-50/20 transition-all duration-300 text-[10px]">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-normal text-[10px] shadow-inner ${lid.isMapped ? 'bg-emerald-50 text-emerald-500' : 'bg-red-50 text-red-500'}`}>
                          {lid.lid.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="font-mono font-normal text-bulky-navy-400 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100 group-hover:bg-white transition-all">
                          {lid.lid}
                        </div>
                      </div>
                    </td>

                    {activeTab === 'all' && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="font-normal text-bulky-navy-800 tracking-tight uppercase text-[11px]">{lid.name || 'Anonymous Terminal'}</span>
                          <span className="text-[9px] font-normal text-gray-400 mt-1 flex items-center gap-1.5 uppercase tracking-wider">
                            <span className="material-icons-round text-xs opacity-50">phone</span>
                            {lid.phoneNumber || 'NULL-IDENTIFIER'}
                          </span>
                        </div>
                      </td>
                    )}

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-6">
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Calls</span>
                          <span className="font-normal text-bulky-navy-600">{lid.callCount}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Msg</span>
                          <span className="font-normal text-bulky-navy-600">{lid.messageCount}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-0.5">Pulsar</span>
                          <span className="font-normal text-bulky-navy-600 text-[9px] uppercase">{formatDate(lid.lastSeen)}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className={`px-2.5 py-1 rounded-lg text-[8px] font-normal uppercase tracking-wider inline-flex items-center gap-2 border ${lid.isMapped ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                        <span className={`w-1 h-1 rounded-full ${lid.isMapped ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        {lid.isMapped ? 'Resolved' : 'Unresolved'}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right pr-8">
                      {editingLid === lid.lid ? (
                        <div className="flex flex-col gap-2 min-w-[200px] ml-auto text-left bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                          <input
                            type="text"
                            placeholder="Network ID"
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                            className="px-3 py-2 bg-white border border-gray-100 rounded-lg text-[10px] font-normal focus:outline-none focus:border-bulky-blue-500 shadow-sm"
                          />
                          <input
                            type="text"
                            placeholder="Designation"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="px-3 py-2 bg-white border border-gray-100 rounded-lg text-[10px] font-normal focus:outline-none focus:border-bulky-blue-500 shadow-sm"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSave(lid.lid)}
                              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded-lg text-[9px] font-normal uppercase tracking-wider transition-all"
                            >
                              Resolve
                            </button>
                            <button
                              onClick={() => setEditingLid(null)}
                              className="bg-white hover:bg-gray-100 text-gray-500 px-3 rounded-lg text-sm font-normal transition-all border border-gray-100"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(lid.lid)}
                            className="w-10 h-10 rounded-xl bg-bulky-navy-800 text-white hover:bg-bulky-blue-600 transition-all flex items-center justify-center shadow-sm active:scale-90"
                            title="Map Terminal"
                          >
                            <span className="material-icons-round text-lg">edit</span>
                          </button>
                          {activeTab === 'all' && (
                            <button
                              onClick={() => handleDelete(lid.lid)}
                              className="w-10 h-10 rounded-xl bg-gray-100 text-red-500 hover:bg-red-500 hover:text-white transition-all flex items-center justify-center shadow-sm active:scale-90"
                              title="Purge Resolution"
                            >
                              <span className="material-icons-round text-lg">delete_outline</span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List */}
          <div className="lg:hidden space-y-4">
            {(activeTab === 'unmapped' ? unmappedLids : allMappings).map((lid) => (
              <div key={lid.lid} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm relative overflow-hidden group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-normal text-[10px] shadow-inner ${lid.isMapped ? 'bg-emerald-50 text-emerald-500' : 'bg-red-50 text-red-500'}`}>
                      {lid.lid.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="font-medium">{lid.lid}</h2>
                      <p className="text-[11px] font-normal text-bulky-navy-800 uppercase tracking-tight">{lid.name || 'Anonymous Terminal'}</p>
                    </div>
                  </div>
                  <div className={`px-2 py-0.5 rounded text-[8px] font-normal uppercase tracking-wider border ${lid.isMapped ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
                    {lid.isMapped ? 'Resolved' : 'Unmapped'}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4 py-3 border-y border-gray-50 bg-gray-50/30 -mx-5 px-5">
                  <div className="flex flex-col">
                    <span className="text-[7.5px] font-normal text-gray-400 uppercase tracking-wider">Calls</span>
                    <span className="font-normal text-bulky-navy-800 text-[10px]">{lid.callCount}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[7.5px] font-normal text-gray-400 uppercase tracking-wider">Msg</span>
                    <span className="font-normal text-bulky-navy-800 text-[10px]">{lid.messageCount}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[7.5px] font-normal text-gray-400 uppercase tracking-wider">Pulsar</span>
                    <span className="font-normal text-bulky-navy-800 text-[9px] truncate uppercase">{formatDate(lid.lastSeen).split(',')[0]}</span>
                  </div>
                </div>

                {editingLid === lid.lid ? (
                  <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <input
                      type="text"
                      placeholder="Network ID"
                      value={formData.phoneNumber}
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-lg text-[10px] font-normal focus:outline-none shadow-sm"
                    />
                    <input
                      type="text"
                      placeholder="Designation"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-lg text-[10px] font-normal focus:outline-none shadow-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(lid.lid)}
                        className="flex-1 bg-bulky-blue-500 text-white py-2.5 rounded-lg text-[9px] font-normal uppercase tracking-wider shadow-lg shadow-bulky-blue-500/10"
                      >
                        Commit Map
                      </button>
                      <button
                        onClick={() => setEditingLid(null)}
                        className="bg-white text-gray-500 px-4 rounded-lg border border-gray-100 font-normal text-xs"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(lid.lid)}
                      className="flex-1 bg-bulky-navy-800 text-white py-2.5 rounded-lg text-[9px] font-normal uppercase tracking-wider flex items-center justify-center gap-2 active:scale-95 shadow-sm"
                    >
                      <span className="material-icons-round text-sm">edit</span>
                      Map Identity
                    </button>
                    {activeTab === 'all' && (
                      <button
                        onClick={() => handleDelete(lid.lid)}
                        className="w-10 bg-red-50 text-red-500 rounded-lg flex items-center justify-center border border-red-100 active:scale-90"
                      >
                        <span className="material-icons-round text-lg">delete_outline</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {(activeTab === 'unmapped' ? unmappedLids : allMappings).length === 0 && (
            <div className="py-20 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-emerald-500">
                <span className="material-icons-round text-3xl">task_alt</span>
              </div>
              <h2 className="font-medium">Matrix Optimized</h2>
              <p className="text-gray-400 text-[9px] font-normal uppercase tracking-wider mt-2">All network terminals identified.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LidMappings;
